import { useState } from "react";
import { Badge } from "../../../../../components/ui/badge";
import { Button } from "../../../../../components/ui/button";
import { Card, CardContent } from "../../../../../components/ui/card";
import { ConfirmDialog } from "../../../../../components/ui/confirm-dialog";
import { Input } from "../../../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../components/ui/table";
import { Textarea } from "../../../../../components/ui/textarea";
import { useToast } from "../../../../../hooks/use-toast";
import {
  mensagemErro,
  useBloqueioMutations,
  useListaBloqueios,
  useOpcoesBloqueio,
} from "../hooks/use-bloqueios";
import {
  Bloqueio,
  BloqueioFiltros,
  BloqueioOrigem,
  BloqueioStatusFiltro,
} from "../types";
import {
  formatDataHora,
  formatDocumento,
  rotuloMotivo,
} from "../utils/bloqueio-utils";
import { BloquearDialog, BloquearModo } from "./bloquear-dialog";

const TODOS = "__todos__";

export function BloqueiosTab({ onChange }: { onChange?: () => void }) {
  const { toast } = useToast();
  const { data: opcoes } = useOpcoesBloqueio();
  const [filtros, setFiltros] = useState<BloqueioFiltros>({
    status: "vigente",
  });
  const [documento, setDocumento] = useState("");
  const [page, setPage] = useState(1);
  const [modo, setModo] = useState<BloquearModo | null>(null);
  const [revogando, setRevogando] = useState<Bloqueio | null>(null);
  const [revogadoMotivo, setRevogadoMotivo] = useState("");

  const { data, isFetching, error } = useListaBloqueios(filtros, page);
  const { desbloquear } = useBloqueioMutations(onChange);

  const mudarFiltro = (patch: Partial<BloqueioFiltros>) => {
    setFiltros((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const confirmarDesbloqueio = async () => {
    if (!revogando) return;
    try {
      await desbloquear.mutateAsync({
        ids: [revogando.id],
        revogado_motivo: revogadoMotivo.trim() || undefined,
      });
      toast({ title: "Bloqueio removido" });
    } catch (e) {
      toast({
        title: "Erro",
        description: mensagemErro(e),
        variant: "destructive",
      });
    } finally {
      setRevogando(null);
      setRevogadoMotivo("");
    }
  };

  const itens = data?.items ?? [];
  const pag = data?.pagination;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              mudarFiltro({ documento: documento.trim() || undefined });
            }}
          >
            <Input
              aria-label="Filtrar por CPF/CNPJ"
              placeholder="CPF/CNPJ"
              value={documento}
              onChange={(e) => setDocumento(e.target.value)}
              className="w-48"
            />
            <Button type="submit" variant="outline">
              Filtrar
            </Button>
          </form>

          <FiltroSelect
            label="Status"
            value={filtros.status ?? "vigente"}
            onChange={(v) => mudarFiltro({ status: v as BloqueioStatusFiltro })}
            opcoes={[
              ["vigente", "Vigentes"],
              ["revogado", "Revogados"],
              ["todos", "Histórico completo"],
            ]}
          />
          <FiltroSelect
            label="Origem"
            value={filtros.origem ?? TODOS}
            onChange={(v) =>
              mudarFiltro({
                origem: v === TODOS ? undefined : (v as BloqueioOrigem),
              })
            }
            opcoes={[
              [TODOS, "Todas as origens"],
              ["gestor", "Gestor"],
              ["cliente", "Cliente"],
            ]}
          />
          <FiltroSelect
            label="Ops"
            value={filtros.ops_entregador ?? TODOS}
            onChange={(v) =>
              mudarFiltro({ ops_entregador: v === TODOS ? undefined : v })
            }
            opcoes={[
              [TODOS, "Todas as ops"],
              ...(opcoes?.ops ?? []).map((o) => [o, o] as [string, string]),
            ]}
          />

          <Button
            className="ml-auto"
            onClick={() => setModo({ tipo: "massa" })}
          >
            Bloqueio em massa
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {error && (
            <p className="mb-4 text-sm text-red-600">{mensagemErro(error)}</p>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entregador</TableHead>
                  <TableHead>Ops</TableHead>
                  <TableHead>Escopo</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Bloqueado por</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itens.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center text-muted-foreground"
                    >
                      {isFetching
                        ? "Carregando..."
                        : "Nenhum bloqueio encontrado"}
                    </TableCell>
                  </TableRow>
                )}
                {itens.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>
                      <div className="font-medium">
                        {formatDocumento(b.numero_documento)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {b.nome_completo ?? "-"}
                      </div>
                    </TableCell>
                    <TableCell>{b.ops}</TableCell>
                    <TableCell>
                      {b.telefone ? `Telefone ${b.telefone}` : "CPF/CNPJ"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={b.origem === "gestor" ? "default" : "outline"}
                      >
                        {b.origem === "gestor" ? "Gestor" : "Cliente"}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      {rotuloMotivo(
                        b.motivo_codigo,
                        b.motivo_texto,
                        opcoes?.motivos,
                      )}
                    </TableCell>
                    <TableCell>
                      <div>{b.criado_por}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatDataHora(b.criado_em)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {b.revogado_em ? (
                        <div className="text-xs text-muted-foreground">
                          Desbloqueado por {b.revogado_por} ({b.revogado_origem}
                          ) em {formatDataHora(b.revogado_em)}
                          {b.revogado_motivo ? ` — ${b.revogado_motivo}` : ""}
                        </div>
                      ) : (
                        <Badge variant="destructive">Vigente</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!b.revogado_em && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setRevogando(b)}
                        >
                          Desbloquear
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {pag && pag.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-end gap-2 text-sm">
              <span>
                Página {pag.page} de {pag.totalPages} ({pag.totalRows})
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Anterior
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= pag.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <BloquearDialog
        modo={modo}
        onClose={() => setModo(null)}
        onDone={onChange}
      />

      <ConfirmDialog
        open={!!revogando}
        onOpenChange={(open) => !open && setRevogando(null)}
        onConfirm={confirmarDesbloqueio}
        title="Remover bloqueio?"
        description={
          revogando
            ? `${formatDocumento(revogando.numero_documento)} na ops ${revogando.ops}${
                revogando.telefone ? `, telefone ${revogando.telefone}` : ""
              } (bloqueio do ${revogando.origem}).`
            : undefined
        }
        confirmText="Desbloquear"
        cancelText="Cancelar"
      >
        <Textarea
          aria-label="Motivo do desbloqueio (opcional)"
          placeholder="Motivo do desbloqueio (opcional)"
          rows={2}
          value={revogadoMotivo}
          onChange={(e) => setRevogadoMotivo(e.target.value)}
        />
      </ConfirmDialog>
    </div>
  );
}

function FiltroSelect({
  label,
  value,
  onChange,
  opcoes,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  opcoes: Array<[string, string]>;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-44" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {opcoes.map(([v, l]) => (
          <SelectItem key={v} value={v}>
            {l}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
